import type { MapGeoJSONFeature } from 'maplibre-gl';

import { type BoatProfile, isUnsafeClearance, isUnsafeDepth } from '../core/boat';
import { formatDepth, formatLatitude, formatLongitude, type UnitPreferences } from '../core/units';
import {
  ANCHORAGE_CATEGORIES,
  BUOY_SHAPES,
  CARDINAL_MARKS,
  COLOURS,
  KIND_LABELS,
  LATERAL_MARKS,
  LAYER_LABELS,
  LIGHT_CHARACTERS,
  RESTRICTIONS,
  SEABED_SURFACES,
  WATER_LEVELS,
  WRECK_CATEGORIES,
  describeCode,
  describeCodeList,
} from '../chart/s57codes';

export interface FeaturePanelProps {
  features: MapGeoJSONFeature[];
  position: { lat: number; lon: number };
  units: UnitPreferences;
  boat: BoatProfile;
  onClose: () => void;
}

interface Described {
  title: string;
  /** Rendered prominently, in red, when the feature endangers this boat. */
  danger: string | null;
  lines: string[];
}

const num = (value: unknown): number | null =>
  (typeof value === 'number' && Number.isFinite(value) ? value : null);

const text = (value: unknown): string | null =>
  (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * Turn one chart feature into words.
 *
 * Ordering matters: the danger line comes first and is never buried among
 * descriptive attributes, because the reason a skipper taps a rock is to find
 * out whether they can pass over it.
 */
export function describeFeature(
  feature: MapGeoJSONFeature,
  units: UnitPreferences,
  boat: BoatProfile,
): Described {
  const layer = feature.sourceLayer ?? '';
  const p = feature.properties ?? {};
  const kind = text(p.kind);

  const title = [
    kind ? KIND_LABELS[kind] ?? kind : null,
    LAYER_LABELS[layer] ?? layer,
  ].filter(Boolean).join(' — ');

  const lines: string[] = [];
  let danger: string | null = null;

  const name = text(p.objnam);
  if (name) lines.push(name);

  switch (layer) {
    case 'hazard': {
      const valsou = num(p.valsou);
      if (valsou === null) {
        danger = 'Least depth not surveyed — treat as dangerous';
      } else {
        const depth = formatDepth(valsou, units.depth);
        if (isUnsafeDepth(valsou, boat)) danger = `Least depth ${depth} — shallower than your safety depth`;
        else lines.push(`Least depth ${depth}`);
      }
      const watlev = describeCode(WATER_LEVELS, p.watlev);
      if (watlev) lines.push(`Water level: ${watlev}`);
      const catwrk = kind === 'wreck' ? describeCode(WRECK_CATEGORIES, p.catwrk) : null;
      if (catwrk) lines.push(catwrk);
      break;
    }

    case 'overhead_obstruction': {
      const verclr = num(p.verclr);
      if (verclr === null) {
        if (boat.airDraftM !== undefined) danger = 'Clearance not charted — height unknown';
      } else {
        const clearance = formatDepth(verclr, units.depth);
        if (isUnsafeClearance(verclr, boat)) danger = `Clearance ${clearance} — lower than your air draft`;
        else lines.push(`Vertical clearance ${clearance}`);
      }
      const closed = num(p.verccl);
      if (closed !== null) lines.push(`Closed clearance ${formatDepth(closed, units.depth)}`);
      break;
    }

    case 'depth_area': {
      const shallow = num(p.drval1);
      const deep = num(p.drval2);
      if (shallow === null) {
        danger = 'Depth range not charted';
      } else {
        const range = deep === null
          ? `${formatDepth(shallow, units.depth)} or deeper`
          : `${formatDepth(shallow, units.depth, { withUnit: false })}–${formatDepth(deep, units.depth)}`;
        if (isUnsafeDepth(shallow, boat)) danger = `Depth ${range} — shallower than your safety depth`;
        else lines.push(`Depth ${range}`);
      }
      break;
    }

    case 'sounding': {
      const depth = num(p.depth);
      if (depth !== null) {
        const shown = formatDepth(depth, units.depth);
        if (isUnsafeDepth(depth, boat)) danger = `Sounding ${shown} — shallower than your safety depth`;
        else lines.push(`Sounding ${shown}`);
      }
      break;
    }

    case 'depth_contour': {
      const valdco = num(p.valdco);
      if (valdco !== null) lines.push(`${formatDepth(valdco, units.depth)} contour`);
      break;
    }

    case 'buoy':
    case 'beacon': {
      const marks = kind === 'cardinal' ? CARDINAL_MARKS : LATERAL_MARKS;
      const category = describeCode(marks, p.catbuoy ?? p.catbeacon);
      if (category) lines.push(category);
      const colour = describeCodeList(COLOURS, p.colour);
      if (colour) lines.push(`Colour: ${colour}`);
      const shape = describeCode(BUOY_SHAPES, p.boyshp);
      if (shape) lines.push(`Shape: ${shape}`);
      break;
    }

    case 'light': {
      const character = describeCode(LIGHT_CHARACTERS, p.litchr);
      const colour = describeCodeList(COLOURS, p.colour);
      const period = num(p.sigper);
      const range = num(p.valnmr);
      const parts = [character, colour, period !== null ? `${period}s period` : null]
        .filter(Boolean).join(', ');
      if (parts) lines.push(parts);
      if (range !== null) lines.push(`Nominal range ${range} NM`);
      const sectr1 = num(p.sectr1);
      const sectr2 = num(p.sectr2);
      if (sectr1 !== null && sectr2 !== null) lines.push(`Sector ${sectr1}°–${sectr2}°`);
      break;
    }

    case 'restricted_area': {
      const restriction = describeCodeList(RESTRICTIONS, p.restrn);
      if (restriction) lines.push(`Restriction: ${restriction}`);
      break;
    }

    case 'anchorage': {
      const category = describeCode(ANCHORAGE_CATEGORIES, p.catach);
      if (category) lines.push(category);
      break;
    }

    case 'seabed_area': {
      const surface = describeCodeList(SEABED_SURFACES, p.natsur);
      if (surface) lines.push(`Seabed: ${surface}`);
      break;
    }

    case 'cable_pipeline': {
      const burial = num(p.burdep);
      if (burial !== null) lines.push(`Buried ${formatDepth(burial, units.depth)}`);
      lines.push('Do not anchor');
      break;
    }

    default:
      break;
  }

  const cell = text(p.cell);
  if (cell) lines.push(`Chart cell ${cell}`);

  return { title, danger, lines };
}

/**
 * Layers that tile the whole chart, so exactly one feature covers any given
 * point per band.
 *
 * These are deduplicated on the layer alone rather than on their values. A tap
 * lands inside one depth area on the general chart and a different one on the
 * harbour chart, and the two disagree — a band 2 cell says "0–10 m, shallower
 * than your safety depth" while the band 4 cell over the same water says
 * "10–20 m". Keying on the depth kept both, and the panel then reported a
 * danger the chart underneath was not drawing: the map renders the
 * largest-scale band on top, so that is the one answer to show. Reporting the
 * small-scale generalisation as well reads as a second, contradictory hazard.
 */
const COVERAGE_LAYERS = new Set(['depth_area', 'inland_water', 'land_area', 'sea_area']);

const bandOf = (feature: MapGeoJSONFeature): number => Number(feature.properties?.band ?? 0);

/**
 * Deduplicate the stack of features under a tap.
 *
 * The same real-world object appears once per usage band it is charted in, and
 * showing a rock three times helps nobody. The largest-scale band is kept since
 * that is the most detailed survey — and, for coverage layers, since it is the
 * one actually drawn on screen.
 *
 * Ties within a band go to the shallower feature. Two depth areas can meet at
 * the tapped pixel, and of the two possible answers the shallow one is the
 * conservative one.
 */
export function dedupeFeatures(features: MapGeoJSONFeature[]): MapGeoJSONFeature[] {
  const seen = new Map<string, MapGeoJSONFeature>();
  for (const feature of features) {
    const p = feature.properties ?? {};
    const layer = feature.sourceLayer ?? '';
    const key = COVERAGE_LAYERS.has(layer)
      ? layer
      : [
        layer,
        p.kind ?? '',
        p.objnam ?? '',
        p.valsou ?? p.depth ?? p.drval1 ?? p.valdco ?? '',
      ].join('|');

    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, feature);
      continue;
    }
    if (bandOf(feature) > bandOf(existing)) {
      seen.set(key, feature);
      continue;
    }
    if (bandOf(feature) === bandOf(existing) && shallower(feature, existing)) {
      seen.set(key, feature);
    }
  }
  return [...seen.values()];
}

/** True when `a` states a shallower least depth than `b`. Unknown is not shallow. */
function shallower(a: MapGeoJSONFeature, b: MapGeoJSONFeature): boolean {
  const depth = (feature: MapGeoJSONFeature): number | null => {
    const p = feature.properties ?? {};
    return num(p.drval1) ?? num(p.valsou) ?? num(p.depth);
  };
  const left = depth(a);
  const right = depth(b);
  return left !== null && right !== null && left < right;
}

export function FeaturePanel({
  features,
  position,
  units,
  boat,
  onClose,
}: FeaturePanelProps): React.JSX.Element {
  const unique = dedupeFeatures(features);
  const described = unique.map((feature) => describeFeature(feature, units, boat));
  // Dangerous features first — that is what the tap was for.
  described.sort((a, b) => Number(Boolean(b.danger)) - Number(Boolean(a.danger)));

  return (
    <div className="sheet sheet--feature" role="dialog" aria-label="Chart feature">
      <div className="sheet__header">
        <h2>{described.length ? 'Chart features' : 'Position'}</h2>
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <p className="sheet__position">
        {formatLatitude(position.lat)}   {formatLongitude(position.lon)}
      </p>

      {described.length === 0 && (
        <p className="sheet__hint">Nothing charted at this point.</p>
      )}

      <ul className="feature-list">
        {described.map((item, index) => (
          <li key={`${item.title}-${index}`} className={item.danger ? 'feature feature--danger' : 'feature'}>
            <span className="feature__title">{item.title}</span>
            {item.danger && <span className="feature__danger">{item.danger}</span>}
            {item.lines.map((line) => (
              <span key={line} className="feature__line">{line}</span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}
